import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Compass, Crown, Trophy, Lock, Check } from '@phosphor-icons/react';
import { DEVOTIONAL_IMAGES } from '../config/devotionalImages';

const API = process.env.REACT_APP_BACKEND_URL;

const LeadersPage = () => {
  const { user, getAuthHeader } = useAuth();
  const [devotionals, setDevotionals] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDevotionals();
  }, [user]);

  const fetchDevotionals = async () => {
    try {
      const headers = user ? getAuthHeader() : {};
      const res = await axios.get(`${API}/api/devotionals`, { headers });
      setDevotionals(res.data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const unlockedCount = devotionals.filter(d => d.is_completed).length;
  const totalCount = devotionals.length;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F3EE] flex items-center justify-center">
        <div className="animate-pulse text-[#6B6560] font-satoshi">Loading leaders...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F3EE] pb-24">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-[#F5F3EE]/95 backdrop-blur-sm border-b border-[#1A1714]/10">
        <div className="container mx-auto px-5 py-4">
          <h1 className="font-instrument text-2xl text-[#1A1714]">Hall of Leaders</h1>
          <p className="text-sm text-[#6B6560] font-satoshi">Biblical wisdom, modern application</p>
        </div>
      </header>

      {/* Progress Section */}
      <section className="px-5 py-6">
        <div className="bg-white rounded-2xl p-5 border border-[#1A1714]/10">
          <div className="flex items-center justify-between mb-3">
            <span className="font-satoshi text-sm text-[#6B6560]">Leaders Unlocked</span>
            <span className="font-instrument text-lg text-[#1A1714]">
              {unlockedCount} of {totalCount}
            </span>
          </div>
          
          {/* Progress Bar */}
          <div className="h-2 bg-[#1A1714]/10 rounded-full overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${(unlockedCount / totalCount) * 100}%` }}
              transition={{ duration: 0.8, ease: 'easeOut' }}
              className="h-full bg-[#B8932B] rounded-full"
            />
          </div>
        </div>
      </section>

      {/* Leaders Grid */}
      <section className="px-5">
        <div className="grid grid-cols-2 gap-4">
          {devotionals.map((devotional, idx) => {
            const isUnlocked = devotional.is_completed;
            
            return (
              <motion.div
                key={devotional.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.05 }}
              >
                {isUnlocked ? (
                  <Link to={`/devotional/${devotional.id}`}>
                    <div className="relative rounded-2xl overflow-hidden aspect-[3/4] group">
                      {/* Background Image */}
                      <div 
                        className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-105"
                        style={{ 
                          backgroundImage: `url(${DEVOTIONAL_IMAGES[devotional.character_image] || DEVOTIONAL_IMAGES.david})`,
                          backgroundColor: '#2A2724'
                        }}
                      />
                      
                      {/* Gradient Overlay */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />
                      
                      {/* Gold Badge */}
                      <div className="absolute top-3 right-3 w-8 h-8 rounded-full bg-[#B8932B] flex items-center justify-center">
                        <Check size={18} weight="bold" className="text-white" />
                      </div>
                      
                      {/* Content */}
                      <div className="absolute bottom-0 left-0 right-0 p-4">
                        <h3 className="font-instrument text-lg text-white mb-1">
                          {devotional.character}
                        </h3>
                        
                        <div className="flex items-center gap-2">
                          <span 
                            className="px-2 py-0.5 rounded text-[10px] font-satoshi text-white/90"
                            style={{ backgroundColor: 'rgba(255,255,255,0.2)' }}
                          >
                            {devotional.testament === 'Old Testament' ? 'OT' : 'NT'}
                          </span>
                          {devotional.quiz_score && (
                            <span className="text-xs text-white/70 font-satoshi">
                              {devotional.quiz_score}%
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </Link>
                ) : (
                  <div className="relative rounded-2xl overflow-hidden aspect-[3/4]">
                    {/* Background Image - Grayscale */}
                    <div 
                      className="absolute inset-0 bg-cover bg-center grayscale opacity-40"
                      style={{ 
                        backgroundImage: `url(${DEVOTIONAL_IMAGES[devotional.character_image] || DEVOTIONAL_IMAGES.david})`,
                        backgroundColor: '#2A2724'
                      }}
                    />
                    
                    {/* Dark Overlay */}
                    <div className="absolute inset-0 bg-black/60" />
                    
                    {/* Lock Icon */}
                    <div className="absolute inset-0 flex flex-col items-center justify-center p-4">
                      <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center mb-3">
                        <Lock size={24} className="text-white/50" />
                      </div>
                      
                      <p className="font-instrument text-white/70 text-center text-sm mb-1">
                        {devotional.character}
                      </p>
                      
                      <p className="text-[10px] text-white/40 font-satoshi text-center">
                        Complete "{devotional.title}" to unlock
                      </p>
                    </div>
                  </div>
                )}
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* Bottom Navigation */}
      <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-[#1A1714]/10 z-50">
        <div className="flex items-center justify-around py-3">
          <Link 
            to="/devotionals" 
            className="flex flex-col items-center gap-1 text-[#6B6560] hover:text-[#1A1714] transition-colors"
          >
            <Compass size={24} />
            <span className="text-xs font-satoshi">Devotionals</span>
          </Link>
          
          <Link 
            to="/devotionals/leaders" 
            className="flex flex-col items-center gap-1 text-[#B8932B]"
          >
            <Crown size={24} weight="fill" />
            <span className="text-xs font-satoshi font-medium">Leaders</span>
          </Link>
          
          <Link 
            to="/devotionals/growth" 
            className="flex flex-col items-center gap-1 text-[#6B6560] hover:text-[#1A1714] transition-colors"
          >
            <Trophy size={24} />
            <span className="text-xs font-satoshi">Growth</span>
          </Link>
        </div>
      </nav>
    </div>
  );
};

export default LeadersPage;
